// councilService.js - Service for handling Council of Elders debates with backend API

class CouncilDebateService {
  constructor() {
    // Use the API endpoint instead of direct OpenAI calls
    this.apiUrl = '/api/council';
  }

  // Generate elder personalities for the system prompt
  getElderPersonality(elder) {
    return `You are ${elder.name}, with the following personality: ${elder.description}.
      You are in a debate. You are NOT a polite collaborator. Your job is to:
      - Hold your position fiercely
      - Mock, dismiss, or aggressively endorse other elders by name
      - Use sharp language: "wrong," "absurd," "exactly right," "naive," "delusional"
      - Never hedge with "I think" or "perhaps" — speak with conviction
      - If you agree with someone, agree LOUDLY and add fuel
      - If you disagree, attack their reasoning, not just state your own view
      - Maximum 2-3 sentences per response. Be punchy.`;
  }

  // Create the debate context
  createDebateContext(question, selectedElders, previousMessages = []) {
    const context = `
      A council of 7 elders is debating the following question: "${question}"
      
      The participating elders are:
      ${selectedElders.map(e => `- ${e.name}: ${e.description}`).join('\n')}
      
      Rules for the debate:
      1. Each elder should speak in character based on their personality
      2. Responses should be 2-3 sentences maximum
      3. Elders should debate vigorously, engaging with each other's points and challenging ideas
      4. The debate should work toward finding a consensus answer
      5. After sufficient discussion, elders should vote on the best answer
      
      Previous messages in this debate:
      ${previousMessages.map(m => `${m.elder}: ${m.content}`).join('\n')}
    `;

    return context;
  }

// Get a single elder's response via API
  async getElderResponse(elder, question, previousMessages, round) {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'elderResponse',
          data: {
            elder,
            question,
            previousMessages,
            round
          }
        })
      });

      if (!response.ok) {
        throw new Error('API call failed');
      }

      const data = await response.json();
      return data.response;
    } catch (error) {
      console.error(`Error getting response for ${elder.name}:`, error);
      return `As ${elder.name}, I believe we need to carefully consider all aspects of this question.`;
    }
  }

  // Orchestrate the full debate
  async orchestrateDebate(question, selectedElders, callbacks) {
    const { onElderTyping, onElderSpeak, onSystemMessage, onDebateComplete } = callbacks;
    const debateMessages = [];
    const speakingOrder = this.determineSpeakingOrder(selectedElders);

    onSystemMessage(`The Council convenes to discuss: "${question}"`);
    await this.delay(1500);

    for (let round = 0; round < 2; round++) {
      for (const elder of speakingOrder) {
        onElderTyping(elder);

        const typingDelay = 1500 + Math.random() * 1500;
        await this.delay(typingDelay);

        const response = await this.getElderResponse(elder, question, debateMessages, round);

        onElderSpeak(elder, response);
        debateMessages.push({ elder: elder.name, content: response });

        await this.delay(1000);
      }
    }

    onSystemMessage(`The Council is deliberating...`);
    await this.delay(2000);

    const votingResult = await this.conductVoting(question, selectedElders, debateMessages);
    onSystemMessage(`The Council has reached a decision...`);

    await this.delay(1000);

    onDebateComplete(votingResult);
  }

  // Determine speaking order (can be randomized or based on personality)
  determineSpeakingOrder(elders) {
    // Shuffle for random order
    return [...elders].sort(() => Math.random() - 0.5);
  }

  // Conduct voting among elders via API
  async conductVoting(question, elders, debateMessages) {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'voting',
          data: {
            question,
            elders,
            debateMessages
          }
        })
      });

      if (!response.ok) {
        throw new Error('Voting API call failed');
      }

      const votingResult = await response.json();
      
      // Ensure we have a valid winner
      if (!votingResult.winner) {
        const randomWinner = elders[Math.floor(Math.random() * elders.length)];
        return {
          winner: randomWinner.name,
          reasoning: "The council reached a decision through deliberation.",
          votes: {}
        };
      }

      return votingResult;
    } catch (error) {
      console.error('Error conducting voting:', error);
      // Fallback to random selection if voting fails
      const randomWinner = elders[Math.floor(Math.random() * elders.length)];
      return {
        winner: randomWinner.name,
        reasoning: "The council reached a decision through deliberation.",
        votes: {}
      };
    }
  }

  // Utility function for delays
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Generate a summary of the debate outcome via API
  async generateDebateSummary(question, winner, reasoning) {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'summary',
          data: {
            question,
            winner,
            reasoning
          }
        })
      });

      if (!response.ok) {
        throw new Error('Summary API call failed');
      }

      const data = await response.json();
      return data.summary;
    } catch (error) {
      console.error('Error generating summary:', error);
      return `The council has spoken. ${winner} presented the winning argument.`;
    }
  }
}

export default CouncilDebateService;
